import { Fragment, useEffect, useRef } from 'react';
import { CountdownCircleTimer } from 'react-countdown-circle-timer';
import { Swiper, SwiperSlide } from 'swiper/react';
import { formatTime } from '@/hooks/use-solver-timer';
import { QUESTION_ACCEPT_DURATION, useSolveQuestion } from '@/context/solve-question-context';
import 'swiper/css';
import type { Swiper as SwiperType } from 'swiper';
import { TimeSpan } from '@/utility/time-span.ts';

type SolveQuestionQuestionsStateProps = {
	onClearCallState: () => void;
	onCompletedTimer: () => void;
	onRejectQuestion: () => void;
	onStartSolving: () => void;
	onOpenSolveBottomSheet: () => void;
	onInspectQuestion: () => void;
	onReportQuestion: () => void;
	questionIsAccepted: boolean;
	setQuestionIsAccepted: (accepted: boolean) => void;
	setIsDisabledQuestionTimer: (disabled: boolean) => void;
};

const QuestionSlide = ({
	question,
	onInspectQuestion,
	onReportQuestion,
	onCompletedTimer,
	questionIsAccepted,
	activeTimer,
	currentQuestionTimer,
	updateCurrentQuestionTimer,
	remainingPassedQuestionCount,
	isResolveMode
}: any) => {
	return (
		<>
			<div
				className="slide-content"
				style={{
					background: `linear-gradient(180deg, rgba(0, 0, 0, 0.00) 50%, rgba(0, 0, 0, 0.50) 100%), linear-gradient(0deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.20) 100%), url('${question?.questionImage}') lightgray 50% / cover no-repeat`,
					backgroundSize: 'contain'
				}}>
				<span className="question-topic">{question?.curriculumName}</span>
				<button
					className="question-inspect-button"
					onClick={onInspectQuestion}>
					<i className="icons-search" />
				</button>

				{!isResolveMode && questionIsAccepted && activeTimer?.isActive && activeTimer?.remainingTime > 0 ? (
					<div className="remaining-time-container">
						Kalan Süre{' '}
						<b
							style={{
								color: activeTimer.remainingTime <= 180 ? 'var(--danger-text)' : 'inherit'
							}}>
							{formatTime(activeTimer.remainingTime)}
						</b>
					</div>
				) : !isResolveMode && currentQuestionTimer && remainingPassedQuestionCount ? (
					<div className="countdown-timer">
						<CountdownCircleTimer
							key={`${question?.interactionId}-${activeTimer}`}
							isPlaying={activeTimer}
							size={36}
							strokeWidth={4}
							duration={QUESTION_ACCEPT_DURATION}
							initialRemainingTime={currentQuestionTimer}
							onUpdate={remainingTime => updateCurrentQuestionTimer(remainingTime)}
							trailColor="#FFF"
							onComplete={onCompletedTimer}
							colors={['var(--text-3a68bc)', '#00AFAA00']}
							colorsTime={[11, 0]}>
							{({ remainingTime }) => <div className="countdown-timer-text">{remainingTime}</div>}
						</CountdownCircleTimer>
					</div>
				) : (
					<Fragment />
				)}
			</div>

			<div className="slide-footer">
				<h6>{question?.curriculumElementUnitName}</h6>
				{!questionIsAccepted && (
					<button
						className="question-report-button"
						onClick={onReportQuestion}>
						<i className="icons-danger-square" />
					</button>
				)}
			</div>
		</>
	);
};

const SolveQuestionQuestionsState = ({
	onCompletedTimer,
	onRejectQuestion,
	onStartSolving,
	onOpenSolveBottomSheet,
	onInspectQuestion,
	onReportQuestion,
	questionIsAccepted
}: SolveQuestionQuestionsStateProps) => {
	const swiperRef = useRef<SwiperType | null>(null);

	const {
		questionPool,
		isQuestionPoolLoading,
		activeSlideIndex,
		setActiveSlideIndex,
		handleStartSolving: contextHandleStartSolving,
		isCreatingExpertQuestion,
		isCreatingPassedQuestion,
		activeTimer,
		currentQuestionTimer,
		updateCurrentQuestionTimer,
		getActiveQuestion,
		remainingPassedQuestionCount,
		isResolveMode
	} = useSolveQuestion();

	const activeQuestion = getActiveQuestion();

	useEffect(() => {
		if (swiperRef.current && questionPool && questionPool.length > 0) {
			const safeIndex = Math.min(activeSlideIndex, questionPool.length - 1);
			if (swiperRef.current.activeIndex !== safeIndex) {
				swiperRef.current.slideTo(safeIndex, TimeSpan.milliSecond.threeHundred);
			}
		}
	}, [activeSlideIndex, questionPool]);

	const handleStartSolvingLocal = async () => {
		if (questionIsAccepted) {
			onOpenSolveBottomSheet();
			return;
		}

		if (!activeQuestion?.questionId || !activeQuestion?.interactionId) {
			return;
		}

		await contextHandleStartSolving(activeQuestion.questionId, activeQuestion.interactionId);

		const timerInfo = activeTimer.getTimerInfo();
		if (timerInfo && timerInfo.remainingTime > 0) {
			onStartSolving();
		}
	};

	const handleRejectQuestionLocal = async () => {
		onRejectQuestion();
	};

	const handleSlideChange = (swiper: SwiperType) => {
		const newIndex = swiper.activeIndex;
		if (newIndex !== activeSlideIndex) {
			setActiveSlideIndex(newIndex);
		}
	};

	const handleSwiperInit = (swiper: SwiperType) => {
		swiperRef.current = swiper;
	};

	if (isQuestionPoolLoading) {
		return <></>;
	}

	return (
		<>
			<div className="swiper-container">
				<Swiper
					spaceBetween={0}
					slidesPerView={1}
					className="question-swiper"
					onSlideChange={handleSlideChange}
					onSwiper={handleSwiperInit}
					allowTouchMove={false}
					initialSlide={activeSlideIndex}>
					{questionPool?.map((question, index) => (
						<SwiperSlide key={question?.interactionId || index}>
							<QuestionSlide
								question={question}
								onInspectQuestion={onInspectQuestion}
								onReportQuestion={onReportQuestion}
								onCompletedTimer={onCompletedTimer}
								questionIsAccepted={questionIsAccepted}
								currentQuestionTimer={currentQuestionTimer}
								updateCurrentQuestionTimer={updateCurrentQuestionTimer}
								activeTimer={question?.interactionId === activeQuestion?.interactionId ? activeTimer : null}
								remainingPassedQuestionCount={remainingPassedQuestionCount}
								isResolveMode={isResolveMode}
							/>
						</SwiperSlide>
					))}
				</Swiper>
			</div>

			<div className="question-action-buttons">
				{!isResolveMode && (
					<button
						className="reject-button"
						onClick={handleRejectQuestionLocal}
						disabled={isCreatingExpertQuestion || isCreatingPassedQuestion}>
						<i className="icons-close-square" />
						{questionIsAccepted ? 'Soruyu İptal Et' : 'Reddet'}
					</button>
				)}

				<button
					className="solve-button"
					onClick={handleStartSolvingLocal}
					disabled={isCreatingExpertQuestion || isCreatingPassedQuestion}>
					<i className="icons-check-square" />
					{isCreatingExpertQuestion ? 'Yükleniyor...' : questionIsAccepted ? 'Devam Et' : 'Çözmeye Başla'}
				</button>
			</div>
		</>
	);
};

export default SolveQuestionQuestionsState;
